import { useToast } from "@/components/ui/use-toast";
import { writeDepartmentBasedAssignments } from "@/schemas/departmentBasedAssignments";
import { writeRoleBasedAssignments } from "@/schemas/roleBasedAssignments";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useContractorsAssignmentsContext } from "../components/ContractorsAssignmentsContext";
import { ReadContractorsAssignees } from "../types";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

export const useUpdateContractorsAssignmentMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    isRoleBasedProject,
    ProjectContractorsService,
    projectContractorsMainQueryKey,
  } = useContractorsAssignmentsContext();

  const writeEntitySchema = useMemo(
    () =>
      isRoleBasedProject
        ? writeRoleBasedAssignments
        : writeDepartmentBasedAssignments,
    [isRoleBasedProject],
  );

  return useMutationWithErrorHandling<
    unknown,
    ApiError,
    ReadContractorsAssignees,
    unknown
  >({
    mutationFn: async (contractorsAssignments) => {
      // calls the API to add and update the assignments
      const operations = contractorsAssignments.map((assignment) =>
        ProjectContractorsService.patch(
          assignment.id,
          writeEntitySchema.parse(assignment),
        ),
      );

      const results = await Promise.allSettled(operations);

      const rejected = results.find((result) => result.status === "rejected");
      if (rejected && rejected.status === "rejected") {
        const error = rejected.reason;
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(
          error instanceof Error ? error.message : String(error),
          400,
        );
      }
    },
    errorTitle: t("contractorsAssignments.errors.update"),
    onSuccess: () => {
      toast({
        title: t("contractorsAssignments.success.update"),
      });
      queryClient.invalidateQueries({
        queryKey: [projectContractorsMainQueryKey],
      });
    },
  });
};
